<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';
import { useI18n } from '@/i18n';
import {
  getThemePreference,
  hasThemeOverride,
  initializeTheme,
  setThemePreference,
  watchSystemTheme
} from '@/utils/theme';

const authStore = useAuthStore();
const router = useRouter();
const { locale, setLocale, t } = useI18n();
const isAuthenticated = computed(() => authStore.isAuthenticated);
const userDisplayName = computed(() => authStore.abbreviation || String(authStore.firstName).slice(0, 8) || 'User');
const isAdminUser = computed(() => authStore.role === 'admin' || authStore.role === 'superuser');
// const canEditDiveSites = computed(() => ['contributor', 'admin', 'superuser'].includes(authStore.role));
const currentLocale = computed(() => locale.value);
const themePreference = ref('system');
const isUserMenuOpen = ref(false);
const userMenuRef = ref(null);
const isLocaleMenuOpen = ref(false);
const localeMenuRef = ref(null);
let stopWatchingSystemTheme = () => {};

function changeTheme(event) {
  const nextPreference = event.target.value;
  themePreference.value = nextPreference;
  setThemePreference(nextPreference);
}

function toggleUserMenu() {
  isUserMenuOpen.value = !isUserMenuOpen.value;
}

function closeUserMenu() {
  isUserMenuOpen.value = false;
}

function toggleLocaleMenu() {
  isLocaleMenuOpen.value = !isLocaleMenuOpen.value;
}

function closeLocaleMenu() {
  isLocaleMenuOpen.value = false;
}

function handleDocumentClick(event) {
  if (isUserMenuOpen.value) {
    const userRoot = userMenuRef.value;
    if (userRoot && !userRoot.contains(event.target)) {
      closeUserMenu();
    }
  }

  if (isLocaleMenuOpen.value) {
    const localeRoot = localeMenuRef.value;
    if (localeRoot && !localeRoot.contains(event.target)) {
      closeLocaleMenu();
    }
  }
}

async function logout() {
  try {
    await authStore.logout();
    closeUserMenu();
    router.replace('/login');
  } catch (error) {
    console.error('Logout failed:', error);
  }
}

async function changeLocale(event) {
  const nextLocale = event.target.value;
  setLocale(nextLocale);

  if (isAuthenticated.value) {
    try {
      await authStore.updatePreferredLocale(nextLocale);
    } catch {
      // Keep UI locale even if persistence fails.
    }
  }
}

onMounted(() => {
  themePreference.value = getThemePreference();
  initializeTheme();
  stopWatchingSystemTheme = watchSystemTheme(() => {
    if (hasThemeOverride()) return;
    themePreference.value = getThemePreference();
    initializeTheme();
  });
  document.addEventListener('click', handleDocumentClick);
});

onBeforeUnmount(() => {
  stopWatchingSystemTheme();
  document.removeEventListener('click', handleDocumentClick);
});

watch(
  () => [isAuthenticated.value, authStore.preferredLocale],
  ([authenticated, preferredLocale]) => {
    if (!authenticated) return;
    const next = String(preferredLocale || '').trim().toLowerCase();
    if (!next) return;
    if (next !== currentLocale.value) {
      setLocale(next);
    }
  },
  { immediate: true }
);

watch(isAuthenticated, (value) => {
  if (!value) {
    closeUserMenu();
  } else {
    closeLocaleMenu();
  }
});

watch(() => router.currentRoute.value.fullPath, () => {
  closeUserMenu();
  closeLocaleMenu();
});
</script>

<template>
  <nav class="auth-top-nav glass-card h2o-fade-up">
    <div><router-link class="nav-link-pill" to="/">{{ t('nav.home') }}</router-link></div>
    <div><router-link class="nav-link-pill" to="/about">{{ t('nav.about') }}</router-link></div>
    <!-- <div><router-link v-if="canEditDiveSites" class="nav-link-pill" to="/dive-sites/create">{{ t('nav.addDiveSite') }}</router-link></div> -->
    <div><router-link v-if="isAdminUser" class="nav-link-pill" to="/admin">{{ t('nav.admin') }}</router-link></div>
    <div v-if="!isAuthenticated" class="nav-end">
      <router-link class="nav-link-pill" to="/login">{{ t('nav.login') }}</router-link>
    </div>
    <div v-if="!isAuthenticated" class="guest-actions">
      <div ref="localeMenuRef" class="locale-menu-wrap">
        <button class="nav-button locale-trigger" type="button" @click.stop="toggleLocaleMenu" aria-label="Select language">
          <span aria-hidden="true">🌐</span>
        </button>
        <div v-if="isLocaleMenuOpen" class="locale-menu-panel glass-card">
          <label class="locale-select in-menu">
            <span>{{ t('language.label') }}</span>
            <select class="glass-select locale-dropdown" :value="currentLocale" @change="changeLocale">
              <option value="en">{{ t('language.english') }}</option>
              <option value="de">{{ t('language.german') }}</option>
              <option value="it">{{ t('language.italian') }}</option>
              <option value="fr">{{ t('language.french') }}</option>
            </select>
          </label>
          <label class="locale-select in-menu menu-field">
            <span>{{ t('theme.label') }}</span>
            <select class="glass-select locale-dropdown" :value="themePreference" @change="changeTheme">
              <option value="system">{{ t('theme.system') }}</option>
              <option value="light">{{ t('theme.light') }}</option>
              <option value="dark">{{ t('theme.dark') }}</option>
            </select>
          </label>
        </div>
      </div>
    </div>

    <div v-if="isAuthenticated" ref="userMenuRef" class="user-menu-wrap">
      <button class="nav-link-pill nav-button" type="button" @click.stop="toggleUserMenu">
        {{ userDisplayName }}
      </button>
      <div v-if="isUserMenuOpen" class="user-menu-panel glass-card">
        <label class="locale-select in-menu">
          <span>{{ t('language.label') }}</span>
          <select class="glass-select locale-dropdown" :value="currentLocale" @change="changeLocale">
            <option value="en">{{ t('language.english') }}</option>
            <option value="de">{{ t('language.german') }}</option>
            <option value="it">{{ t('language.italian') }}</option>
            <option value="fr">{{ t('language.french') }}</option>
          </select>
        </label>
        <label class="locale-select in-menu menu-field">
          <span>{{ t('theme.label') }}</span>
          <select class="glass-select locale-dropdown" :value="themePreference" @change="changeTheme">
            <option value="system">{{ t('theme.system') }}</option>
            <option value="light">{{ t('theme.light') }}</option>
            <option value="dark">{{ t('theme.dark') }}</option>
          </select>
        </label>
        <div class="menu-actions">
          <router-link class="btn btn-outline-secondary btn-sm" to="/user">{{ t('nav.editProfile') }}</router-link>
          <button class="btn btn-outline-danger btn-sm" type="button" @click="logout">{{ t('nav.logout') }}</button>
        </div>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.auth-top-nav {
  display: flex;
  gap: 10px;
  align-items: center;
  position: relative;
  z-index: 3000;
  margin: 10px auto 10px;
  padding: 10px 14px;
  max-width: min(1160px, calc(100% - 16px));
  border-radius: 16px;
}

.auth-top-nav a {
  text-decoration: none;
}

.nav-link-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.38rem 0.86rem;
  border-radius: 999px;
  color: var(--h2o-text-soft);
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: background-color 0.25s ease, color 0.25s ease, transform 0.2s ease;
}

.nav-link-pill:hover {
  color: var(--h2o-text);
  background: var(--h2o-pill-hover);
  transform: translateY(-1px);
}

.auth-top-nav a.router-link-active {
  color: var(--h2o-text);
  background: var(--h2o-pill-active);
}

.nav-button {
  border: 0;
  background: transparent;
  cursor: pointer;
}

.nav-end {
  margin-left: auto;
}

.guest-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.locale-select {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--h2o-text-soft);
  font-size: 0.93rem;
}

.user-menu-wrap {
  position: relative;
  margin-left: auto;
}

.locale-menu-wrap {
  position: relative;
}

.locale-trigger {
  /* border: 1px solid rgba(146, 211, 242, 0.34); */
  padding: 1px 1px;
  font-size: 1rem;
  line-height: 1;
}

.menu-field {
  margin-top: 10px;
}

.user-menu-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 260px;
  padding: 12px;
  z-index: 3200;
  pointer-events: auto;
}

.locale-menu-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 220px;
  padding: 12px;
  z-index: 3200;
  pointer-events: auto;
}

.in-menu {
  width: 100%;
  justify-content: space-between;
}

.menu-actions {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.locale-dropdown {
  min-width: 132px;
  padding: 0.32rem 0.5rem;
}

</style>
